import { useState, useEffect, useCallback, useMemo } from 'react';
import { Settings, RotateCcw, Save, Check } from 'lucide-react';
import { showSuccessMessage, showErrorMessage } from '@/shared/components/toast-system';
import './SystemSettings.css';

import {
  getSystemSettings,
  updateSystemSetting,
  resetSystemSetting,
} from '../../../utils/systemSettingsApiUtils';

/**
 * Human-readable labels for setting keys.
 * If a key isn't listed here, we fall back to splitting the PascalCase key.
 */
const SETTING_LABELS = {
  HumanizerEnabled: 'Humanizer',
  SupportAssistantEnabled: 'Support Assistant',
  SupportEmail: 'Support Email',
  CustomerServiceEmail: 'Customer Service Email',
  StripeFreeTrialDays: 'Stripe Free Trial Days',
  TrialNotificationDays: 'Trial Notification Days',
  SpeechTextEnabled: 'Speech to Text',
  QualityLoopEnabled: 'Quality Loop (Judge/Revise)',
  QualityLoopMaxRevisions: 'Quality Loop Max Revisions',
};

/**
 * Explicit ordering for non-boolean settings.
 * Keys listed earlier appear first; unlisted keys follow in their original order.
 */
const NON_BOOLEAN_ORDER = [
  'StripeFreeTrialDays',
  'TrialNotificationDays',
  'QualityLoopMaxRevisions',
  'SupportEmail',
  'CustomerServiceEmail',
];

const humanizeKey = (key) =>
  SETTING_LABELS[key] || key.replace(/([A-Z])/g, ' $1').trim();

const SystemSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingIds, setSavingIds] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [editValues, setEditValues] = useState({});

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await getSystemSettings();
      setSettings(list);

      const initial = {};
      list.forEach((s) => {
        if (s.valueType !== 'boolean') {
          initial[s.id] = String(s.value);
        }
      });
      setEditValues(initial);
    } catch (err) {
      console.error('Failed to load system settings', err);
      setError('Failed to load system settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // ── Toggle a boolean setting (saves immediately) ────────────────────
  const handleToggle = async (setting) => {
    if (savingIds.has(setting.id)) return;

    const newValue = !setting.value;

    setSavingIds((prev) => new Set(prev).add(setting.id));
    try {
      const updated = await updateSystemSetting(setting.id, String(newValue));
      setSettings((prev) =>
        prev.map((s) => (s.id === setting.id ? updated : s))
      );
      showSuccessMessage(`${humanizeKey(setting.key)} ${newValue ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      console.error('Failed to toggle setting', err);
      showErrorMessage('Failed to update setting.');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(setting.id);
        return next;
      });
    }
  };

  // ── Save ALL dirty non-boolean settings at once ─────────────────────
  const handleSaveAll = async () => {
    if (bulkSaving) return;

    const dirtySettings = otherSettings.filter(
      (s) =>
        editValues[s.id] !== undefined &&
        editValues[s.id] !== String(s.value)
    );

    if (dirtySettings.length === 0) return;

    setBulkSaving(true);
    const ids = dirtySettings.map((s) => s.id);
    setSavingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });

    const results = await Promise.allSettled(
      dirtySettings.map((s) => updateSystemSetting(s.id, editValues[s.id]))
    );

    let successCount = 0;
    const failures = [];

    results.forEach((result, idx) => {
      const setting = dirtySettings[idx];
      if (result.status === 'fulfilled') {
        const updated = result.value;
        setSettings((prev) =>
          prev.map((s) => (s.id === setting.id ? updated : s))
        );
        setEditValues((prev) => ({ ...prev, [setting.id]: String(updated.value) }));
        successCount++;
      } else {
        console.error('Failed to save setting', setting.key, result.reason);
        failures.push({
          key: setting.key,
          message: result.reason?.response?.data?.message,
        });
      }
    });

    const failCount = failures.length;

    if (successCount > 0) {
      showSuccessMessage(
        successCount === 1
          ? '1 setting updated.'
          : `${successCount} settings updated.`
      );
    }
    if (failCount > 0) {
      const detail = failures
        .filter((f) => f.message)
        .map((f) => `${humanizeKey(f.key)}: ${f.message}`)
        .join(' ');
      const summary =
        failCount === 1
          ? 'Failed to update 1 setting.'
          : `Failed to update ${failCount} settings.`;
      showErrorMessage(detail ? `${summary} ${detail}` : summary);
    }

    setSavingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setBulkSaving(false);
  };

  const handleReset = async (setting) => {
    if (savingIds.has(setting.id)) return;

    setSavingIds((prev) => new Set(prev).add(setting.id));
    try {
      const updated = await resetSystemSetting(setting.id);
      setSettings((prev) =>
        prev.map((s) => (s.id === setting.id ? updated : s))
      );
      if (updated.valueType !== 'boolean') {
        setEditValues((prev) => ({ ...prev, [setting.id]: String(updated.value) }));
      }
      showSuccessMessage(`${humanizeKey(setting.key)} reset to default.`);
    } catch (err) {
      console.error('Failed to reset setting', err);
      showErrorMessage('Failed to reset setting.');
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev);
        next.delete(setting.id);
        return next;
      });
    }
  };

  const isNonDefault = (setting) => {
    return String(setting.value) !== String(setting.defaultValue);
  };

  const booleanSettings = settings.filter((s) => s.valueType === 'boolean');

  const otherSettings = useMemo(() => {
    const nonBool = settings.filter((s) => s.valueType !== 'boolean');
    return nonBool.sort((a, b) => {
      const idxA = NON_BOOLEAN_ORDER.indexOf(a.key);
      const idxB = NON_BOOLEAN_ORDER.indexOf(b.key);
      // Unlisted keys get a high index so they sort after listed ones
      const orderA = idxA === -1 ? NON_BOOLEAN_ORDER.length : idxA;
      const orderB = idxB === -1 ? NON_BOOLEAN_ORDER.length : idxB;
      return orderA - orderB;
    });
  }, [settings]);

  const hasAnyDirty = otherSettings.some(
    (s) =>
      editValues[s.id] !== undefined &&
      editValues[s.id] !== String(s.value)
  );

  if (loading) {
    return (
      <div className="ss-page">
        <div className="ss-card">
          <div className="ss-skeleton-title" />
          <div className="ss-skeleton-line" />
          <div className="ss-skeleton-line" />
          <div className="ss-skeleton-line-short" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ss-page">
        <div className="ss-card ss-error-card">
          <p className="ss-error-text">{error}</p>
          <button className="ss-btn-retry" onClick={fetchSettings}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ss-page">
      <div className="ss-card">
        {}
        <div className="ss-header">
          <div className="ss-header-left">
            <Settings size={20} className="ss-header-icon" />
            <h2 className="ss-title">System Settings</h2>
          </div>
        </div>

        {/* ── Feature Toggles (save immediately) ──────────────────── */}
        {booleanSettings.length > 0 && (
          <>
            <div className="ss-divider" />
            <div className="ss-section">
              <h3 className="ss-section-label">Feature Toggles</h3>
              {booleanSettings.map((setting) => {
                const isSaving = savingIds.has(setting.id);
                return (
                  <div className="ss-toggle-row" key={setting.id}>
                    <div className="ss-toggle-info">
                      <span className="ss-toggle-label">
                        {humanizeKey(setting.key)}
                        {setting.value && (
                          <span className="ss-status-badge">
                            <Check size={12} /> Enabled
                          </span>
                        )}
                      </span>
                      {setting.description && (
                        <span className="ss-toggle-desc">{setting.description}</span>
                      )}
                    </div>
                    <div className="ss-toggle-controls">
                      <div className="toggle-wrapper">
                        <label className={`toggle-switch ${isSaving ? 'disabled' : ''}`}>
                          <input
                            type="checkbox"
                            className="toggle-input"
                            checked={!!setting.value}
                            onChange={() => handleToggle(setting)}
                            disabled={isSaving}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                        <span className="toggle-status-text">
                          {isSaving ? (
                            <span className="ss-saving-inline">
                              <span className="spinner-small"></span> Saving...
                            </span>
                          ) : setting.value ? 'On' : 'Off'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ── Configuration Values (single save button) ───────────── */}
        {otherSettings.length > 0 && (
          <>
            <div className="ss-divider" />
            <div className="ss-section">
              <h3 className="ss-section-label">Configuration</h3>

              <div className="ss-config-container">
                {otherSettings.map((setting) => {
                  const isSaving = savingIds.has(setting.id);
                  const nonDefault = isNonDefault(setting);
                  return (
                    <div className="ss-field-row" key={setting.id}>
                      <div className="ss-field-header">
                        <label className="ss-field-label" htmlFor={`ss-${setting.id}`}>
                          {humanizeKey(setting.key)}
                        </label>
                        {setting.description && (
                          <span className="ss-field-desc">{setting.description}</span>
                        )}
                      </div>
                      <div className="ss-field-input-row">
                        <input
                          id={`ss-${setting.id}`}
                          type={setting.valueType === 'number' ? 'number' : 'text'}
                          className="ss-field-input"
                          value={editValues[setting.id] ?? ''}
                          onChange={(e) =>
                            setEditValues((prev) => ({
                              ...prev,
                              [setting.id]: e.target.value,
                            }))
                          }
                          disabled={isSaving}
                        />
                        {nonDefault && (
                          <button
                            className="ss-btn ss-btn-reset"
                            onClick={() => handleReset(setting)}
                            disabled={isSaving}
                            title={`Reset to default (${setting.defaultValue})`}
                          >
                            <RotateCcw size={14} />
                          </button>
                        )}
                      </div>
                      <span className="ss-field-default">
                        Default: {setting.defaultValue}
                      </span>
                    </div>
                  );
                })}

                {}
                <div className="ss-config-actions">
                  <button
                    className="ss-btn ss-btn-save-all"
                    onClick={handleSaveAll}
                    disabled={!hasAnyDirty || bulkSaving}
                  >
                    {bulkSaving ? (
                      <span className="ss-saving-inline">
                        <span className="spinner-small"></span> Saving…
                      </span>
                    ) : (
                      <>
                        <Save size={14} /> Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SystemSettings;